<template>
  <el-dialog
    v-model="visible"
    width="480px"
    :fullscreen="isMobile"
    :close-on-click-modal="false"
    align-center
    class="login-dialog"
    @close="resetForms"
  >
    <div class="ld-title">
      {{
        activeTab === "login" ? "登录" : activeTab === "register" ? "注册" : "重置密码"
      }}
    </div>

    <div v-show="activeTab === 'login'" class="ld-panel">
      <el-form
        ref="loginFormRef"
        class="ld-form"
        :model="loginForm"
        :rules="loginRules"
        label-width="0"
      >
        <el-form-item prop="email">
          <el-input v-model="loginForm.email" placeholder="请输入邮箱地址" size="large">
            <template #prefix>
              <el-icon><Message /></el-icon>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item prop="password">
          <el-input
            v-model="loginForm.password"
            type="password"
            placeholder="请输入密码"
            size="large"
            show-password
            @keyup.enter="handleLogin"
          >
            <template #prefix>
              <el-icon><Lock /></el-icon>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item prop="captcha">
          <div class="ld-captcha">
            <el-input
              v-model="loginForm.captcha"
              class="ld-captcha-input"
              placeholder="请输入验证码"
              size="large"
              @keyup.enter="handleLogin"
            >
              <template #prefix>
                <el-icon><Key /></el-icon>
              </template>
            </el-input>
            <div class="ld-captcha-img" @click="refreshCaptcha">
              <img v-if="captchaImageUrl" :src="captchaImageUrl" alt="验证码" />
              <span v-else class="ld-muted">加载中...</span>
            </div>
          </div>
        </el-form-item>
        <el-form-item>
          <div class="ld-login-row">
            <el-checkbox v-model="loginForm.remember">记住我</el-checkbox>
            <el-button type="primary" link @click="handleForgotPassword"
              >忘记密码？</el-button
            >
          </div>
        </el-form-item>
        <el-form-item>
          <el-button
            type="primary"
            size="large"
            class="ld-submit"
            :loading="loginLoading"
            @click="handleLogin"
          >
            登录
          </el-button>
        </el-form-item>
      </el-form>

      <el-divider class="ld-divider">或使用第三方登录</el-divider>

      <div class="ld-oauth">
        <div class="ld-oauth-item" @click="redirectOAuth('QQ')">
          <i class="iconfont icon-QQ ld-oauth-icon ld-oauth-icon--qq"></i>
          <span class="ld-muted">QQ</span>
        </div>
        <div class="ld-oauth-item" @click="redirectOAuth('GITEE')">
          <i class="iconfont icon-gitee ld-oauth-icon ld-oauth-icon--gitee"></i>
          <span class="ld-muted">Gitee</span>
        </div>
      </div>

      <div class="ld-foot">
        <span class="ld-muted">还没有账号？</span>
        <el-button type="primary" link @click="switchToRegister">去注册</el-button>
      </div>
    </div>

    <div v-show="activeTab === 'register'" class="ld-panel">
      <el-form
        ref="registerFormRef"
        class="ld-form"
        :model="registerForm"
        :rules="registerRules"
        label-width="0"
      >
        <el-form-item prop="email">
          <el-input
            v-model="registerForm.email"
            placeholder="请输入邮箱地址"
            size="large"
          >
            <template #prefix>
              <el-icon><Message /></el-icon>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item prop="code">
          <el-input
            v-model="registerForm.code"
            placeholder="请输入邮箱验证码"
            size="large"
          >
            <template #prefix>
              <el-icon><Key /></el-icon>
            </template>
            <template #append>
              <el-button
                type="primary"
                class="ld-send-code"
                :disabled="codeCountdown > 0 || !canSendCode"
                @click="sendCode"
              >
                {{ codeCountdown > 0 ? `${codeCountdown}秒` : "发送验证码" }}
              </el-button>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item prop="password">
          <el-input
            v-model="registerForm.password"
            type="password"
            placeholder="请输入密码（至少8位）"
            size="large"
            show-password
            @input="checkPasswordStrength"
          >
            <template #prefix>
              <el-icon><Lock /></el-icon>
            </template>
          </el-input>
          <div v-if="passwordStrength" class="ld-hint" :class="strengthClass">
            {{ passwordStrength }}
          </div>
        </el-form-item>
        <el-form-item prop="confirmPassword">
          <el-input
            v-model="registerForm.confirmPassword"
            type="password"
            placeholder="请再次输入密码"
            size="large"
            show-password
            @input="checkPasswordMatch"
          >
            <template #prefix>
              <el-icon><Lock /></el-icon>
            </template>
          </el-input>
          <div v-if="passwordMatchError" class="ld-hint ld-hint--err">
            {{ passwordMatchError }}
          </div>
        </el-form-item>
        <el-form-item prop="agreeTerms">
          <el-checkbox v-model="registerForm.agreeTerms" class="ld-agree">
            我已阅读并同意
            <el-button type="primary" link @click="showTerms">《用户协议》</el-button>
            和
            <el-button type="primary" link @click="showPrivacy">《隐私政策》</el-button>
          </el-checkbox>
        </el-form-item>
        <el-form-item>
          <el-button
            type="primary"
            size="large"
            class="ld-submit"
            :loading="registerLoading"
            @click="handleRegister"
          >
            注册
          </el-button>
        </el-form-item>
      </el-form>

      <div class="ld-foot">
        <span class="ld-muted">已有账号？</span>
        <el-button type="primary" link @click="switchToLogin">去登录</el-button>
      </div>
    </div>

    <div v-show="activeTab === 'forgot'" class="ld-panel">
      <el-form
        ref="forgotFormRef"
        class="ld-form"
        :model="forgotForm"
        :rules="forgotRules"
        label-width="0"
      >
        <el-form-item prop="email">
          <el-input v-model="forgotForm.email" placeholder="请输入注册邮箱" size="large">
            <template #prefix>
              <el-icon><Message /></el-icon>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item prop="code">
          <el-input v-model="forgotForm.code" placeholder="请输入邮箱验证码" size="large">
            <template #prefix>
              <el-icon><Key /></el-icon>
            </template>
            <template #append>
              <el-button
                type="primary"
                class="ld-send-code"
                :disabled="codeCountdown > 0 || !canSendForgotCode"
                @click="sendForgotCode"
              >
                {{ codeCountdown > 0 ? `${codeCountdown}秒` : "发送验证码" }}
              </el-button>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item prop="password">
          <el-input
            v-model="forgotForm.password"
            type="password"
            placeholder="新密码（至少8位）"
            size="large"
            show-password
            @input="checkForgotPasswordStrength"
          >
            <template #prefix>
              <el-icon><Lock /></el-icon>
            </template>
          </el-input>
          <div v-if="forgotPasswordStrength" class="ld-hint" :class="forgotStrengthClass">
            {{ forgotPasswordStrength }}
          </div>
        </el-form-item>
        <el-form-item prop="confirmPassword">
          <el-input
            v-model="forgotForm.confirmPassword"
            type="password"
            placeholder="请再次输入新密码"
            size="large"
            show-password
            @input="checkForgotPasswordMatch"
          >
            <template #prefix>
              <el-icon><Lock /></el-icon>
            </template>
          </el-input>
          <div v-if="forgotPasswordMatchError" class="ld-hint ld-hint--err">
            {{ forgotPasswordMatchError }}
          </div>
        </el-form-item>
        <el-form-item>
          <el-button
            type="primary"
            size="large"
            class="ld-submit"
            :loading="forgotLoading"
            @click="handleResetPassword"
          >
            重置密码
          </el-button>
        </el-form-item>
      </el-form>

      <div class="ld-foot">
        <el-button type="primary" link @click="switchToLogin">返回登录</el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { ElMessage } from "element-plus";
import { Message, Lock, Key } from "@element-plus/icons-vue";
import {
  loginByEmail,
  getOauthAuthUrl,
  sendEmailCode,
  register,
  resetPassword,
} from "@/api/login";
import {
  clearSavedLoginEmail,
  getSavedLoginEmail,
  saveLoginEmailForRemember,
  setToken,
} from "@/utils/authStorage";
import request from "@/utils/request";

const EMAIL_RULES = [
  { required: true, message: "请输入邮箱地址", trigger: "blur" },
  { type: "email", message: "请输入正确的邮箱地址", trigger: "blur" },
];

export default {
  name: "LoginDialog",
  components: { Message, Lock, Key },
  props: {
    modelValue: { type: Boolean, default: false },
    defaultTab: { type: String, default: "login" },
  },
  emits: ["update:modelValue", "success"],
  data() {
    return {
      activeTab: "login",
      loginLoading: false,
      registerLoading: false,
      forgotLoading: false,
      loginForm: {
        email: "",
        password: "",
        captcha: "",
        captchaKey: "",
        remember: false,
      },
      registerForm: {
        email: "",
        code: "",
        password: "",
        confirmPassword: "",
        agreeTerms: false,
      },
      forgotForm: {
        email: "",
        code: "",
        password: "",
        confirmPassword: "",
      },
      captchaImageUrl: "",
      passwordStrength: "",
      passwordMatchError: "",
      forgotPasswordStrength: "",
      forgotPasswordMatchError: "",
      codeCountdown: 0,
      codeTimer: null,
      isMobile: false,
      loginRules: {
        email: EMAIL_RULES,
        password: [{ required: true, message: "请输入密码", trigger: "blur" }],
        captcha: [{ required: true, message: "请输入验证码", trigger: "blur" }],
      },
    };
  },
  computed: {
    visible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    canSendCode() {
      return (
        this.registerForm.email &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.registerForm.email)
      );
    },
    canSendForgotCode() {
      return (
        this.forgotForm.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.forgotForm.email)
      );
    },
    forgotStrengthClass() {
      if (!this.forgotPasswordStrength) return "";
      if (this.forgotPasswordStrength.includes("弱")) return "ld-hint--weak";
      if (this.forgotPasswordStrength.includes("中")) return "ld-hint--medium";
      return "ld-hint--strong";
    },
    strengthClass() {
      if (!this.passwordStrength) return "";
      if (this.passwordStrength.includes("弱")) return "ld-hint--weak";
      if (this.passwordStrength.includes("中")) return "ld-hint--medium";
      return "ld-hint--strong";
    },
    registerRules() {
      return {
        email: EMAIL_RULES,
        code: [
          { required: true, message: "请输入邮箱验证码", trigger: "blur" },
          { validator: this.validateRegisterCode, trigger: "blur" },
        ],
        password: [
          { required: true, message: "请输入密码", trigger: "blur" },
          { validator: this.validateRegisterPassword, trigger: "blur" },
        ],
        confirmPassword: [
          { required: true, message: "请再次输入密码", trigger: "blur" },
          { validator: this.validateConfirmPassword, trigger: "blur" },
        ],
        agreeTerms: [{ validator: this.validateAgreeTerms, trigger: "change" }],
      };
    },
    forgotRules() {
      return {
        email: EMAIL_RULES,
        code: [
          { required: true, message: "请输入邮箱验证码", trigger: "blur" },
          { validator: this.validateRegisterCode, trigger: "blur" },
        ],
        password: [
          { required: true, message: "请输入新密码", trigger: "blur" },
          { validator: this.validateRegisterPassword, trigger: "blur" },
        ],
        confirmPassword: [
          { required: true, message: "请再次输入新密码", trigger: "blur" },
          { validator: this.validateForgotConfirmPassword, trigger: "blur" },
        ],
      };
    },
  },
  watch: {
    modelValue(open) {
      if (!open) return;
      this.activeTab = this.defaultTab;
      if (this.activeTab === "login") {
        this.refreshCaptcha();
        this.applyRememberedLoginUi();
      }
    },
    "registerForm.email"() {
      this.stopCountdown();
    },
    "forgotForm.email"() {
      this.stopCountdown();
    },
  },
  mounted() {
    this.isMobile = window.innerWidth <= 768;
  },
  methods: {
    clearValidate(tab) {
      this.$nextTick(() => {
        const map = {
          login: "loginFormRef",
          register: "registerFormRef",
          forgot: "forgotFormRef",
        };
        this.$refs[map[tab]]?.clearValidate();
      });
    },
    switchToLogin() {
      this.stopCountdown();
      this.activeTab = "login";
      if (!this.captchaImageUrl) this.refreshCaptcha();
      this.applyRememberedLoginUi();
      this.clearValidate("login");
    },
    switchToRegister() {
      this.stopCountdown();
      this.activeTab = "register";
      this.clearValidate("register");
    },
    switchToForgot() {
      this.stopCountdown();
      this.activeTab = "forgot";
      this.forgotForm.email = this.loginForm.email || "";
      this.forgotForm.code = "";
      this.forgotForm.password = "";
      this.forgotForm.confirmPassword = "";
      this.forgotPasswordStrength = "";
      this.forgotPasswordMatchError = "";
      this.clearValidate("forgot");
    },
    applyRememberedLoginUi() {
      const saved = getSavedLoginEmail();
      if (saved) {
        this.loginForm.email = saved;
        this.loginForm.remember = true;
      }
    },
    resetForms() {
      const saved = getSavedLoginEmail();
      this.loginForm = {
        email: saved,
        password: "",
        captcha: "",
        captchaKey: "",
        remember: !!saved,
      };
      this.captchaImageUrl = "";
      this.$refs.loginFormRef?.resetFields();
      this.$refs.loginFormRef?.clearValidate();

      this.registerForm = {
        email: "",
        code: "",
        password: "",
        confirmPassword: "",
        agreeTerms: false,
      };
      this.passwordStrength = "";
      this.passwordMatchError = "";
      this.stopCountdown();
      this.$refs.registerFormRef?.resetFields();
      this.$refs.registerFormRef?.clearValidate();

      this.forgotForm = {
        email: "",
        code: "",
        password: "",
        confirmPassword: "",
      };
      this.forgotPasswordStrength = "";
      this.forgotPasswordMatchError = "";
      this.$refs.forgotFormRef?.resetFields();
      this.$refs.forgotFormRef?.clearValidate();
    },
    handleLogin() {
      this.$refs.loginFormRef.validate(async (valid) => {
        if (!valid) return;
        this.loginLoading = true;
        try {
          const { data } = await loginByEmail(
            this.loginForm.email,
            this.loginForm.password,
            this.loginForm.captcha,
            this.loginForm.captchaKey
          );
          if (data.code !== 200) {
            ElMessage.error(data.msg || "登录失败");
            this.refreshCaptcha();
            return;
          }
          const token = data.data;
          setToken(token, this.loginForm.remember);
          if (this.loginForm.remember) {
            saveLoginEmailForRemember(this.loginForm.email);
          } else {
            clearSavedLoginEmail();
          }
          ElMessage.success("登录成功");
          this.visible = false;
          this.$emit("success", "login");
          window.dispatchEvent(
            new CustomEvent("login-state-change", { detail: { isLogin: true } })
          );
        } catch (e) {
          console.error("登录失败:", e);
          ElMessage.error(e.response?.data?.msg || "登录失败，请稍后重试");
          this.refreshCaptcha();
        } finally {
          this.loginLoading = false;
        }
      });
    },
    handleRegister() {
      this.$refs.registerFormRef.validate(async (valid) => {
        if (!valid) return;
        this.registerLoading = true;
        try {
          const { data } = await register(
            this.registerForm.email,
            this.registerForm.code,
            this.registerForm.password
          );
          if (data.code !== 200) {
            ElMessage.error(data.msg || "注册失败，请稍后重试");
            return;
          }
          ElMessage.success(data.msg || "注册成功！");
          setTimeout(() => {
            this.activeTab = "login";
            this.loginForm.email = this.registerForm.email;
            this.refreshCaptcha();
          }, 1000);
        } catch (e) {
          console.error("注册失败:", e);
          ElMessage.error("注册失败，请稍后重试");
        } finally {
          this.registerLoading = false;
        }
      });
    },
    handleForgotPassword() {
      this.switchToForgot();
    },
    sendForgotCode() {
      if (!this.canSendForgotCode) {
        ElMessage.warning("请先输入正确的邮箱地址");
        return;
      }
      if (this.codeCountdown > 0) return;
      this.startCountdown();
      sendEmailCode(this.forgotForm.email)
        .then(({ data }) => {
          if (data.code === 200) {
            ElMessage.success(data.msg || "验证码已发送到您的邮箱，请查收");
          } else {
            this.stopCountdown();
            ElMessage.error(data.msg || "验证码发送失败，请稍后重试");
          }
        })
        .catch((error) => {
          this.stopCountdown();
          console.error("发送验证码失败:", error);
          ElMessage.error("验证码发送失败，请稍后重试");
        });
    },
    checkForgotPasswordStrength() {
      const pwd = this.forgotForm.password;
      if (!pwd) {
        this.forgotPasswordStrength = "";
        return;
      }
      let s = 0;
      if (pwd.length >= 8) s++;
      if (pwd.length >= 12) s++;
      if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) s++;
      if (/\d/.test(pwd)) s++;
      if (/[^a-zA-Z0-9]/.test(pwd)) s++;
      if (s <= 2) this.forgotPasswordStrength = "密码强度：弱";
      else if (s <= 3) this.forgotPasswordStrength = "密码强度：中";
      else this.forgotPasswordStrength = "密码强度：强";
    },
    checkForgotPasswordMatch() {
      const { password, confirmPassword } = this.forgotForm;
      this.forgotPasswordMatchError =
        confirmPassword && password !== confirmPassword ? "两次输入的密码不一致" : "";
    },
    handleResetPassword() {
      this.$refs.forgotFormRef.validate(async (valid) => {
        if (!valid) return;
        this.forgotLoading = true;
        try {
          const { data } = await resetPassword(
            this.forgotForm.email,
            this.forgotForm.code,
            this.forgotForm.password
          );
          if (data.code !== 200) {
            ElMessage.error(data.msg || "重置失败，请稍后重试");
            return;
          }
          ElMessage.success(data.msg || "密码已重置");
          this.loginForm.email = this.forgotForm.email;
          this.activeTab = "login";
          this.forgotForm = {
            email: "",
            code: "",
            password: "",
            confirmPassword: "",
          };
          this.forgotPasswordStrength = "";
          this.forgotPasswordMatchError = "";
          this.stopCountdown();
          this.refreshCaptcha();
          this.$nextTick(() => {
            this.$refs.forgotFormRef?.resetFields();
            this.$refs.forgotFormRef?.clearValidate();
          });
        } catch (e) {
          console.error("重置密码失败:", e);
          ElMessage.error(e.response?.data?.msg || "重置失败，请稍后重试");
        } finally {
          this.forgotLoading = false;
        }
      });
    },
    async redirectOAuth(type) {
      const label = type === "GITEE" ? "Gitee" : "QQ";
      try {
        const { data } = await getOauthAuthUrl(type);
        if (data.code !== 200) {
          ElMessage.error(data.msg || "获取授权地址失败");
          return;
        }
        const { state, authorizeUrl } = data.data;
        sessionStorage.setItem(`oauth_state_${type.toLowerCase()}`, state);
        sessionStorage.setItem("oauth_return_path", this.$route.fullPath);
        window.location.href = authorizeUrl;
      } catch (e) {
        console.error(`${label}登录失败:`, e);
        ElMessage.error(`${label}登录失败，请稍后重试`);
      }
    },
    refreshCaptcha() {
      this.loginForm.captcha = "";
      this.loginForm.captchaKey = "";
      request
        .get("/api/captcha")
        .then(({ data }) => {
          const d = data.data || {};
          if (d.captchaKey) this.loginForm.captchaKey = d.captchaKey;
          if (d.imageBase64)
            this.captchaImageUrl = `data:image/jpeg;base64,${d.imageBase64}`;
        })
        .catch((error) => {
          console.error("获取验证码失败:", error);
          ElMessage.error("获取验证码失败，请刷新页面重试");
        });
    },
    checkPasswordStrength() {
      const pwd = this.registerForm.password;
      if (!pwd) {
        this.passwordStrength = "";
        return;
      }
      let s = 0;
      if (pwd.length >= 8) s++;
      if (pwd.length >= 12) s++;
      if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) s++;
      if (/\d/.test(pwd)) s++;
      if (/[^a-zA-Z0-9]/.test(pwd)) s++;
      if (s <= 2) this.passwordStrength = "密码强度：弱";
      else if (s <= 3) this.passwordStrength = "密码强度：中";
      else this.passwordStrength = "密码强度：强";
    },
    checkPasswordMatch() {
      const { password, confirmPassword } = this.registerForm;
      this.passwordMatchError =
        confirmPassword && password !== confirmPassword ? "两次输入的密码不一致" : "";
    },
    sendCode() {
      if (!this.canSendCode) {
        ElMessage.warning("请先输入正确的邮箱地址");
        return;
      }
      if (this.codeCountdown > 0) return;
      this.startCountdown();
      sendEmailCode(this.registerForm.email)
        .then(({ data }) => {
          if (data.code === 200) {
            ElMessage.success(data.msg || "验证码已发送到您的邮箱，请查收");
          } else {
            this.stopCountdown();
            ElMessage.error(data.msg || "验证码发送失败，请稍后重试");
          }
        })
        .catch((error) => {
          this.stopCountdown();
          console.error("发送验证码失败:", error);
          ElMessage.error("验证码发送失败，请稍后重试");
        });
    },
    startCountdown() {
      this.stopCountdown();
      this.codeCountdown = 60;
      this.codeTimer = setInterval(() => {
        this.codeCountdown--;
        if (this.codeCountdown <= 0) this.stopCountdown();
      }, 1000);
    },
    stopCountdown() {
      if (this.codeTimer) clearInterval(this.codeTimer);
      this.codeTimer = null;
      this.codeCountdown = 0;
    },
    /** public/legal 下静态页，构建后位于 dist/legal，与站点同源 */
    legalDocUrl(filename) {
      const publicBase = process.env.BASE_URL || "/";
      return new URL(`legal/${filename}`, window.location.origin + publicBase).href;
    },
    showTerms() {
      window.open(
        this.legalDocUrl("user-agreement.html"),
        "_blank",
        "noopener,noreferrer"
      );
    },
    showPrivacy() {
      window.open(
        this.legalDocUrl("privacy-policy.html"),
        "_blank",
        "noopener,noreferrer"
      );
    },
    validateRegisterPassword(rule, value, callback) {
      if (!value || value.length < 8) callback(new Error("密码长度至少8位"));
      else callback();
    },
    validateConfirmPassword(rule, value, callback) {
      if (value !== this.registerForm.password)
        callback(new Error("两次输入的密码不一致"));
      else callback();
    },
    validateRegisterCode(rule, value, callback) {
      if (!value) callback(new Error("请输入邮箱验证码"));
      else if (value.length !== 6) callback(new Error("验证码为6位数字"));
      else callback();
    },
    validateAgreeTerms(rule, value, callback) {
      if (!value) callback(new Error("请同意用户协议和隐私政策"));
      else callback();
    },
    validateForgotConfirmPassword(rule, value, callback) {
      if (value !== this.forgotForm.password) callback(new Error("两次输入的密码不一致"));
      else callback();
    },
  },
  beforeUnmount() {
    this.stopCountdown();
  },
};
</script>

<style lang="scss" scoped>
.login-dialog {
  .ld-title {
    margin: 0 0 16px;
    text-align: center;
    font-size: 18px;
    font-weight: 600;
    line-height: 1.4;
    color: var(--el-text-color-primary);
  }

  .ld-panel {
    padding: 0 4px;
  }
  .ld-form {
    margin-top: 0;
  }
  .ld-captcha {
    display: flex;
    gap: 12px;
    align-items: center;
    width: 100%;
  }
  .ld-captcha-input {
    flex: 1;
  }
  .ld-captcha-img {
    width: 110px;
    height: 40px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--el-border-color);
    border-radius: 8px;
    cursor: pointer;
    background: var(--el-fill-color-lighter);
    &:hover {
      background: var(--el-fill-color-light);
    }
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 6px;
    }
  }
  .ld-login-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }
  .ld-submit {
    width: 100%;
    height: 44px;
  }
  .ld-divider {
    margin: 24px 0;
  }
  .ld-oauth {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 28px;
    margin-bottom: 20px;
  }
  .ld-oauth-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.2s;
    &:hover {
      background: var(--el-fill-color-lighter);
    }
  }
  .ld-oauth-icon {
    font-size: 28px;
    &--qq {
      color: #12b7f5;
    }
    &--gitee {
      color: #c71d23;
    }
  }
  .ld-foot {
    text-align: center;
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid var(--el-border-color-lighter);
    .ld-muted {
      margin-right: 4px;
    }
  }
  .ld-muted {
    font-size: 13px;
    color: var(--el-text-color-secondary);
  }
  .ld-hint {
    margin-top: 8px;
    font-size: 13px;
    &--weak {
      color: #ef4444;
    }
    &--medium {
      color: #eab308;
    }
    &--strong {
      color: #22c55e;
    }
    &--err {
      color: var(--el-color-danger);
    }
  }
  .ld-agree {
    font-size: 14px;
    color: var(--el-text-color-secondary);
    :deep(.el-checkbox__label) {
      display: inline-flex;
      flex-wrap: wrap;
      align-items: center;
      column-gap: 2px;
      line-height: 1.5;
    }
    :deep(.el-button) {
      margin-left: 0 !important;
      padding: 0;
      vertical-align: baseline;
    }
  }
  .ld-send-code {
    white-space: nowrap;
    font-size: 13px;
  }

  @media (max-width: 768px) {
    .ld-title {
      margin-bottom: 14px;
      font-size: 17px;
    }

    .ld-panel {
      padding: 0;
    }
    /* 验证码输入与图片保持同一行，输入区可收缩避免挤出 */
    .ld-captcha {
      flex-direction: row;
      align-items: center;
      gap: 8px;
      min-width: 0;
    }
    .ld-captcha-input {
      min-width: 0;
      flex: 1;
    }
    .ld-captcha-input :deep(.el-input__wrapper) {
      min-width: 0;
    }
    .ld-captcha-img {
      width: 96px;
      height: 40px;
      flex-shrink: 0;
    }
    .ld-login-row {
      flex-wrap: wrap;
      gap: 8px;
    }
    .ld-oauth {
      gap: 20px;
      margin-bottom: 16px;
    }
    .ld-foot {
      margin-top: 16px;
      padding-top: 16px;
    }
    .ld-divider {
      margin: 18px 0;
      :deep(.el-divider__text) {
        white-space: nowrap;
        padding: 0 10px;
        font-size: 13px;
      }
    }
    .ld-agree {
      align-items: center;
      line-height: 1.5;
      :deep(.el-checkbox__label) {
        white-space: normal;
        line-height: 1.5;
      }
    }
  }
}
</style>
